import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './interactiveViewerModal.css';
import Viewer           from './viewer';
import SaveConfirmModal from './SaveConfirmModal';
import IPSLocations     from 'containers/PreEForm/PreEFormFields/IPSLocations';
import Button           from 'components/Button';
import { projectStatusCodeMap }     from 'components/ProjectStatusCodeMap';
import { isViewOnlyOrExternalView } from 'utils/UserInfo';
import { useViewerToken }           from 'hooks/useViewerToken';
import useViewerModel               from 'hooks/useViewerModel';
import useLatestRef                 from 'hooks/useLatestRef';
import { DEFAULT_HIGHLIGHT_IDS, COLOR_SCROLLABLE } from '../../utils/ViewerConstants';
import { toUrlSafeUrn, log, getViewerErrorMessage, getPlatform } from '../../utils/Viewer';

// ─────────────────────────────────────────────────────────────
// DRAG_SELECT_HINTS — platform-keyed instruction strings.
// Defined at module scope so the object is never re-created on
// render. Keyed by the three values getPlatform() can return:
//   'mac' | 'windows' | 'other'
// ─────────────────────────────────────────────────────────────
const DRAG_SELECT_HINTS = {
  mac: {
    key:   '⌘ Command',
    label: 'Hold ⌘ Command and drag to multi-select assets',
  },
  windows: {
    key:   'Ctrl',
    label: 'Hold Ctrl and drag (left mouse button) to multi-select assets',
  },
  other: {
    key:   'Ctrl / ⌘',
    label: 'Hold Ctrl (Win) or ⌘ Command (Mac) and drag to multi-select assets',
  },
};

// ─────────────────────────────────────────────────────────────
// ViewerStatusMessage — loading / error / no-URN states
//
// Props:
//   message  — string to display
//   isError  — switches icon and CSS modifier to error styling
//   onRetry  — optional; when provided renders a "Try again"
//              button so the user can recover from a token
//              failure without closing and reopening the modal
// ─────────────────────────────────────────────────────────────
const ViewerStatusMessage = ({ message, isError, onRetry }) => (
  <div className={`viewer-status-message${isError ? ' viewer-status-message--error' : ''}`}>
    <div>
      <div className="viewer-status-icon">{isError ? '⚠️' : '⏳'}</div>
      <div>{message}</div>
      {onRetry && (
        <button
          className="viewer-retry-btn"
          onClick={onRetry}
          aria-label="Retry loading the viewer"
        >
          Try again
        </button>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ViewerRefreshBanner — unobtrusive top-of-viewer strip shown
// during background token refreshes.  The 3D scene stays fully
// alive; this is purely informational.
//
// Rendered with role="status" and aria-live="polite" so screen
// readers announce it without interrupting the user.
// ─────────────────────────────────────────────────────────────
const ViewerRefreshBanner = () => (
  <div
    className="viewer-refresh-banner"
    role="status"
    aria-live="polite"
  >
    🔄 Refreshing viewer session…
  </div>
);

// ─────────────────────────────────────────────────────────────
// DragSelectTooltip — floating hint anchored to the top-right
// corner of the viewer pane. Collapses to a compact icon badge
// at rest and expands to the full instruction on hover / focus
// so it never obscures the 3D model.
//
// Props:
//   platform — 'mac' | 'windows' | 'other'
//              Passed from the parent (computed once at modal
//              mount via useMemo) so this child never has to
//              re-derive it.
// ─────────────────────────────────────────────────────────────
const DragSelectTooltip = ({ platform }) => {
  const hint = DRAG_SELECT_HINTS[platform] ?? DRAG_SELECT_HINTS.other;

  return (
    <div
      className="drag-select-tooltip"
      role="tooltip"
      aria-label={hint.label}
    >
      {/* Collapsed state — always visible in the viewer corner */}
      <div className="drag-select-tooltip__trigger" tabIndex={0}>
        <span className="drag-select-tooltip__icon" aria-hidden="true">
          🖱️
        </span>
        <span className="drag-select-tooltip__key-badge">
          {hint.key}
        </span>
      </div>

      {/* Expanded panel — revealed on hover / focus-within via CSS */}
      <div className="drag-select-tooltip__panel" role="status">
        <div className="drag-select-tooltip__panel-title">
          Multi-Select
        </div>
        <div className="drag-select-tooltip__panel-body">
          {hint.label}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ViewerLegend — color legend + selectable / checked / grouped badges.
//
// Props:
//   goldAssetIds      — Set of all selectable asset IDs in model
//   mappedAssetIds    — Set of mapped asset IDs
//   visibleMappings   — filtered mappings visible in the sidebar
//   viewerCheckedCount — count of checked assets in the model
//   hasPartialGroups  — true when any grouped attribute has only
//                       some of its IDs checked (shows orange)
// ─────────────────────────────────────────────────────────────
const ViewerLegend = ({
  goldAssetIds,
  mappedAssetIds,
  visibleMappings,
  viewerCheckedCount,
  hasPartialGroups,
}) => (
  <>
    <div className="viewer-color-legend">
      <div className="viewer-color-legend__title">Component Colors</div>

      <div className="legend-item">
        <div className="legend-color-box legend-color-box--gold" />
        <span>Selectable Assets</span>
      </div>

      {visibleMappings.length > 0 && (
        <div className="legend-item">
          <div className="legend-color-box legend-color-box--purple" />
          <span>Checked Mapped Assets</span>
        </div>
      )}

      {goldAssetIds.size > mappedAssetIds.size && (
        <div className="legend-item">
          <div className="legend-color-box legend-color-box--cyan" />
          <span>Checked Non-Mapped Assets</span>
        </div>
      )}

      {hasPartialGroups && (
        <div className="legend-item">
          <div className="legend-color-box legend-color-box--orange" />
          <span>Partially Checked Grouped Assets</span>
        </div>
      )}
    </div>

    <div className="viewer-badges-container">
      {goldAssetIds.size > 0 && (
        <div className="drag-select-badge drag-select-badge--info">
          {goldAssetIds.size} selectable asset{goldAssetIds.size !== 1 ? 's' : ''}
        </div>
      )}
      {viewerCheckedCount > 0 && (
        <div className="drag-select-badge drag-select-badge--asset">
          {viewerCheckedCount} asset{viewerCheckedCount !== 1 ? 's' : ''} checked
        </div>
      )}
      {hasPartialGroups && (
        <div className="drag-select-badge drag-select-badge--grouped">
          Grouped — partial selection
        </div>
      )}
    </div>
  </>
);

// ─────────────────────────────────────────────────────────────
// ViewerActions — Save / Cancel / Close buttons
// ─────────────────────────────────────────────────────────────
const ViewerActions = ({ isReadOnly, saveButtonText, projectStatus, onSave, onClose }) => {
  if (isReadOnly) {
    return <Button onClickHandler={onClose}>Close</Button>;
  }
  if (
    projectStatus !== projectStatusCodeMap['CLOSED'] &&
    !isViewOnlyOrExternalView()
  ) {
    return (
      <>
        <Button onClickHandler={onSave}>{saveButtonText}</Button>
        <Button onClickHandler={onClose}>Cancel</Button>
      </>
    );
  }
  return null;
};

// ─────────────────────────────────────────────────────────────
// InteractiveViewerModal
// ─────────────────────────────────────────────────────────────
const InteractiveViewerModal = (props) => {
  const {
    preEForm,
    preEFormMappings,
    project,
    urn,
    onViewerSave,
    saveVisibleText = 'Save',
    setShow,
    highlightIdList = DEFAULT_HIGHLIGHT_IDS,
    onCheckboxChange,
    autoCheckOnViewerSelection = true,
  } = props;

  // ── Credentials ──────────────────────────────────────────────
  // isRefreshing — true during a silent background token rotation;
  //   the viewer stays mounted and functional throughout.
  // retry        — stable callback; wired to the "Try again" button
  //   shown after a hard token failure.
  const { token, expiresIn, loading, error, isRefreshing, retry } = useViewerToken();

  const urlSafeUrn         = urn ? toUrlSafeUrn(urn) : null;
  const isReadOnly         = !saveVisibleText;
  const saveButtonText     = typeof saveVisibleText === 'string' ? saveVisibleText : 'Save';
  const saveVisibleTextRef = useLatestRef(saveVisibleText);

  // ── getToken — stable callback for <Viewer> ───────────────────
  // The Autodesk SDK calls getAccessToken whenever it needs a token.
  // We back it with a ref so APS always gets the live values without
  // the viewer being torn down and rebuilt on every token rotation.
  //
  // expiresIn is mirrored into a ref for the same reason: we need the
  // latest TTL inside getToken without adding it to useCallback's deps
  // (which would recreate getToken and cause a new viewer start).
  const expiresInRef = useRef(expiresIn);
  useEffect(() => { expiresInRef.current = expiresIn; }, [expiresIn]);

  const getToken = useCallback((onTokenReady) => {
    if (token) onTokenReady(token, expiresInRef.current ?? 3600);
  }, [token]);
  // Note: token intentionally stays in deps so that when the hook
  // delivers a rotated token string getToken is recreated — the
  // Viewer component's internal ref picks up the new version without
  // a re-mount (see viewer.js getTokenRef pattern).

  // ── Platform — computed once at mount ────────────────────────
  // useMemo with [] deps so getPlatform() is called exactly once
  // regardless of how many times the modal re-renders.
  // Passed to DragSelectTooltip as a stable prop so the child
  // never has to re-derive it.
  const platform = useMemo(() => getPlatform(), []);

  // ── Form state ───────────────────────────────────────────────
  const [saveConfirmOpen,       setSaveConfirmOpen]       = useState(false);
  const [scrollableSelectedIds, setScrollableSelectedIds] = useState([]);

  // ── Local copy of preEFormMappings (modal-only changes) ──────
  // Deep-cloned on mount so parent re-renders don't close the modal.
  const [localPreEFormMappings, setLocalPreEFormMappings] = useState(() =>
    JSON.parse(JSON.stringify(preEFormMappings ?? [])),
  );

  // ── Checked asset IDs — initialised from pre-selected assets ─
  const [checkedAssetIds, setCheckedAssetIds] = useState(() => {
    const initialChecked = [];
    (preEFormMappings ?? []).forEach((mapping) => {
      (mapping.assets ?? []).forEach((asset) => {
        if (asset.selected) {
          const id = String(asset.ipsAssetId ?? asset.assetName ?? '').trim();
          if (id) initialChecked.push(id);
        }
      });
    });
    log('[Init] Pre-selected assets:', initialChecked);
    return initialChecked;
  });

  // ── Stable ref to checkedAssetIds ────────────────────────────
  // Allows callbacks to read the current array synchronously without
  // adding it to deps arrays (which would cause stale-closure loops).
  const checkedAssetIdsRef = useLatestRef(checkedAssetIds);

  // ── Immutable snapshot of what was selected when modal opened ─
  // Never mutated after mount. Passed to SaveConfirmModal so it
  // can derive which assets were unchecked this session and
  // display them in red as a removal confirmation.
  const [initialCheckedAssetIds] = useState(() => {
    const initial = [];
    (preEFormMappings ?? []).forEach((mapping) => {
      (mapping.assets ?? []).forEach((asset) => {
        if (asset.selected) {
          const id = String(asset.ipsAssetId ?? asset.assetName ?? '').trim();
          if (id) initial.push(id);
        }
      });
    });
    return initial;
  });

  // ── Viewer model hook ────────────────────────────────────────
  const {
    viewerRef,
    allAttributesRef,
    assetIdToNameRef,
    assetIdToCalloutNumberRef,
    goldDbIdsRef,
    goldAssetIds,
    mappedAssetIds,
    hasPartialGroups,
    viewerSelectedDbIds,
    setViewerSelectedDbIds,
    isUpdatingFromViewerRef,
    handleViewerReady,
    applyIdListColors,
    resolveDbId,
    getAssetIdsFromDbId,
    highlightMultipleAssets,
  } = useViewerModel({
    preEFormMappings,
    highlightIdList,
    checkedAssetIds,
    autoCheckOnViewerSelection,
    saveVisibleTextRef,
  });

  // ─────────────────────────────────────────────────────────────
  // visibleMappings — assets filtered to only those found in
  // the open model (same logic as IPSLocations).
  // ─────────────────────────────────────────────────────────────
  const visibleMappings = useMemo(() => {
    if (!goldAssetIds.size) return localPreEFormMappings;
    return localPreEFormMappings.reduce((acc, mapping) => {
      const selectableAssets = (mapping.assets ?? []).filter((asset) => {
        const id = String(asset.ipsAssetId ?? '').trim();
        return id && goldAssetIds.has(id);
      });
      if (selectableAssets.length > 0) acc.push({ ...mapping, assets: selectableAssets });
      return acc;
    }, []);
  }, [localPreEFormMappings, goldAssetIds]);

  // ─────────────────────────────────────────────────────────────
  // visibleAssetIdSet — flat Set of every ipsAssetId present in
  // visibleMappings (i.e. mapped gold assets only). Single source
  // of truth for "is this asset in the sidebar?" across all handlers.
  // ─────────────────────────────────────────────────────────────
  const visibleAssetIdSet = useMemo(() => {
    const ids = new Set();
    visibleMappings.forEach((mapping) => {
      (mapping.assets ?? []).forEach((asset) => {
        const id = String(asset.ipsAssetId ?? '').trim();
        if (id) ids.add(id);
      });
    });
    return ids;
  }, [visibleMappings]);

  // ─────────────────────────────────────────────────────────────
  // viewerCheckedCount — assets that are checked AND in the model
  // ─────────────────────────────────────────────────────────────
  const viewerCheckedCount = useMemo(() => {
    let count = 0;
    localPreEFormMappings.forEach((mapping) => {
      mapping.assets.forEach((asset) => {
        const id = String(asset.ipsAssetId ?? '').trim();
        if (asset.selected && id && goldAssetIds.has(id)) count++;
      });
    });
    return count;
  }, [localPreEFormMappings, goldAssetIds]);

  // ─────────────────────────────────────────────────────────────
  // localOnChangeAssetId — toggles a single asset in local state.
  // Owns localPreEFormMappings (accordion UI + asset.selected) ONLY.
  // checkedAssetIds is toggled exclusively by handleAssetCheckboxChange
  // (individual clicks) and localSelectAllAssets (select-all).
  // ─────────────────────────────────────────────────────────────
  const localOnChangeAssetId = useCallback((selectedAsset, selectedIpsLocId, setActive, setHeight) => {
    if (!saveVisibleTextRef.current) return;

    const assetIdStr = String(selectedAsset.ipsAssetId ?? selectedAsset.assetName ?? '').trim();
    if (!assetIdStr) return;

    if (!visibleAssetIdSet.has(assetIdStr)) {
      log(`[localOnChangeAssetId] Blocked — "${assetIdStr}" is not in visibleMappings`);
      return;
    }

    setLocalPreEFormMappings((prevMappings) =>
      prevMappings.map((mapping) => {
        const updatedAssets = mapping.assets.map((asset) => {
          if (
            asset.assetId !== selectedAsset.assetId ||
            (asset.formId && asset.formId !== 0 && asset.formId !== preEForm?.id)
          ) {
            return asset;
          }
          return { ...asset, selected: !asset.selected };
        });

        const allAssetsSelected = updatedAssets.every((a) => a.selected);
        const isTarget          = mapping.ipsLocationId === selectedIpsLocId;

        return {
          ...mapping,
          assets: updatedAssets,
          allAssetsSelected,
          setActive: isTarget ? setActive : null,
          setHeight: isTarget ? setHeight : '0px',
        };
      }),
    );
  }, [preEForm?.id, saveVisibleTextRef, visibleAssetIdSet]);

  // ─────────────────────────────────────────────────────────────
  // localSelectAllAssets — toggles all assets in a location,
  // scoped exclusively to visibleMappings.
  // ─────────────────────────────────────────────────────────────
  const localSelectAllAssets = useCallback((ipsLocationId) => {
    if (!saveVisibleTextRef.current) return;

    const targetMapping = localPreEFormMappings.find((m) => m.ipsLocationId === ipsLocationId);
    if (!targetMapping) return;

    const allSelected = !targetMapping.allAssetsSelected;

    const assetIdsToToggle = targetMapping.assets
      .filter((a) =>
        (!a.formId || a.formId === 0 || a.formId === preEForm?.id) &&
        visibleAssetIdSet.has(String(a.ipsAssetId ?? a.assetName ?? '').trim()),
      )
      .map((a) => String(a.ipsAssetId ?? a.assetName ?? '').trim())
      .filter(Boolean);

    const updatedChecked = allSelected
      ? [...new Set([...checkedAssetIdsRef.current, ...assetIdsToToggle])]
      : checkedAssetIdsRef.current.filter((id) => !assetIdsToToggle.includes(id));
    setCheckedAssetIds(updatedChecked);

    requestAnimationFrame(() => {
      isUpdatingFromViewerRef.current = true;
      highlightMultipleAssets(updatedChecked, false); // false = skip fitToView on select-all
      setTimeout(() => {
        isUpdatingFromViewerRef.current = false;
        setViewerSelectedDbIds([]);
      }, 150);
    });

    setLocalPreEFormMappings((prevMappings) =>
      prevMappings.map((mapping) => {
        if (mapping.ipsLocationId !== ipsLocationId) return mapping;

        const visibleAssetsInMapping = mapping.assets.filter((a) =>
          visibleAssetIdSet.has(String(a.ipsAssetId ?? '').trim()),
        );
        const allVisibleSelected = allSelected
          ? true
          : visibleAssetsInMapping.every((a) => a.selected);

        return {
          ...mapping,
          assets: mapping.assets.map((asset) => {
            const id         = String(asset.ipsAssetId ?? asset.assetName ?? '').trim();
            const isVisible  = visibleAssetIdSet.has(id);
            const isEligible = !asset.formId || asset.formId === 0 || asset.formId === preEForm?.id;
            return isVisible && isEligible ? { ...asset, selected: allSelected } : asset;
          }),
          allAssetsSelected: allVisibleSelected,
        };
      }),
    );
  }, [
    preEForm?.id,
    saveVisibleTextRef,
    highlightMultipleAssets,
    visibleAssetIdSet,
    localPreEFormMappings,
    isUpdatingFromViewerRef,
    setViewerSelectedDbIds,
    checkedAssetIdsRef,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // handleAssetCheckboxChange — sidebar checkbox → viewer colors
  // ─────────────────────────────────────────────────────────────
  const handleAssetCheckboxChange = useCallback((asset) => {
    if (isUpdatingFromViewerRef.current) return;
    if (!saveVisibleTextRef.current)     return;
    if (!asset) return;

    const assetIdStr = String(asset.ipsAssetId ?? asset.assetName ?? '').trim();
    if (!assetIdStr) return;

    if (!visibleAssetIdSet.has(assetIdStr)) {
      log(`[handleAssetCheckboxChange] Blocked — "${assetIdStr}" is not in visibleMappings`);
      return;
    }

    const dbId   = resolveDbId(assetIdStr);
    const isGold = dbId !== undefined && goldDbIdsRef.current.has(dbId);

    onCheckboxChange?.(asset);

    const alreadyChecked = checkedAssetIdsRef.current.includes(assetIdStr);
    const nextChecked    = alreadyChecked
      ? checkedAssetIdsRef.current.filter((id) => id !== assetIdStr)
      : [...new Set([...checkedAssetIdsRef.current, assetIdStr])];
    setCheckedAssetIds(nextChecked);
    // Color update is owned entirely by useViewerModel's useEffect which
    // watches checkedAssetIds — no applyIdListColors call needed here.

    // Viewport navigation only — no selection event emitted by fitToView.
    if (!alreadyChecked && isGold && dbId !== undefined) {
      requestAnimationFrame(() => {
        viewerRef.current?.fitToView([dbId]);
      });
    } else if (!isGold) {
      log(`[Checkbox] "${assetIdStr}" toggled (visible but no model dbId)`);
    }
  }, [
    resolveDbId,
    onCheckboxChange,
    isUpdatingFromViewerRef,
    saveVisibleTextRef,
    goldDbIdsRef,
    viewerRef,
    visibleAssetIdSet,
    checkedAssetIdsRef,       // ✅ source of truth for toggle direction
    // asset.selected          ✅ not used — POST-toggle value, would invert direction
    // applyIdListColors       ✅ not called — useViewerModel's effect owns colors
    // allAttributesRef        ✅ not needed — no color call here
    // mappedAssetIds          ✅ not needed — no color call here
    // setViewerSelectedDbIds  ✅ not needed — no viewer.select() call
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // handleScrollableSelection — sidebar scroll → viewer highlight
  // ─────────────────────────────────────────────────────────────
  const handleScrollableSelection = useCallback((selectedIds) => {
    setScrollableSelectedIds(selectedIds);

    if (!saveVisibleTextRef.current) return;

    const viewer = viewerRef.current;
    if (!viewer || !allAttributesRef.current.length) return;

    // Build a Set<string> of checked asset IDs for O(1) lookup and
    // pass it directly to applyIdListColors (no longer dbId-based).
    const checkedAssetIdSet = new Set(checkedAssetIds);
    applyIdListColors(
      viewer,
      allAttributesRef.current,
      highlightIdList,
      checkedAssetIdSet,
      mappedAssetIds,
    );

    const matchingDbIds = [];
    allAttributesRef.current.forEach(({ dbId, properties }) => {
      if (!goldDbIdsRef.current.has(dbId)) return;
      const matched = properties.some(
        (prop) =>
          selectedIds.includes(String(prop.displayValue ?? '')) ||
          selectedIds.includes(String(prop.attributeName ?? '')) ||
          selectedIds.includes(String(dbId)),
      );
      if (matched) {
        matchingDbIds.push(dbId);
        // Only apply scrollable highlight if none of this node's asset
        // IDs are already checked — preserves existing check colors.
        const dbAssetIds = getAssetIdsFromDbId(dbId);
        const anyChecked = [...dbAssetIds].some((id) => checkedAssetIdSet.has(id));
        if (!anyChecked) {
          viewer.setThemingColor(dbId, COLOR_SCROLLABLE(), viewer.model, true);
        }
      }
    });

    if (matchingDbIds.length) {
      viewer.select(matchingDbIds);
      viewer.fitToView(matchingDbIds);
    }
  }, [
    highlightIdList,
    applyIdListColors,
    checkedAssetIds,
    getAssetIdsFromDbId,
    mappedAssetIds,
    saveVisibleTextRef,
    viewerRef,
    allAttributesRef,
    goldDbIdsRef,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // Auto-check effect — syncs viewer selection → checkboxes.
  // Guarded by isUpdatingFromViewerRef to prevent feedback loops.
  //
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!autoCheckOnViewerSelection || !viewerSelectedDbIds.length || isUpdatingFromViewerRef.current) {
      return;
    }

    isUpdatingFromViewerRef.current = true;

    // Expand each selected dbId to ALL of its asset IDs so grouped
    // attributes are toggled as a complete unit.
    const selectedAssetIds = viewerSelectedDbIds.flatMap((dbId) => [
      ...getAssetIdsFromDbId(dbId),
    ]).filter(Boolean);

    const mappedSelectedIds = selectedAssetIds.filter((id) =>
      visibleAssetIdSet.has(id),
    );

    const nonMappedSelectedIds = selectedAssetIds.filter((id) =>
      goldAssetIds.has(id) && !visibleAssetIdSet.has(id),
    );

    const allToggleIds = [...mappedSelectedIds, ...nonMappedSelectedIds];

    if (allToggleIds.length === 0) {
      isUpdatingFromViewerRef.current = false;
      setViewerSelectedDbIds([]);
      return;
    }

    // ── Build asset → selected map from localPreEFormMappings ────
    const assetSelectedMap = new Map();
    localPreEFormMappings.forEach((mapping) => {
      mapping.assets.forEach((a) => {
        const id = String(a.ipsAssetId ?? '').trim();
        if (id) assetSelectedMap.set(id, a.selected);
      });
    });

    // ✅ Fix: compute toggle direction synchronously from
    //    checkedAssetIdsRef.current (always the committed value)
    //    and assetSelectedMap BEFORE any setState call, so the
    //    concrete boolean is available in the forEach below.
    const mappedAllChecked    = mappedSelectedIds.every((id) => assetSelectedMap.get(id) === true);
    const nonMappedAllChecked = nonMappedSelectedIds.every((id) =>
      checkedAssetIdsRef.current.includes(id),
    );
    const allCurrentlyChecked = mappedAllChecked && nonMappedAllChecked;

    // Update checkedAssetIds — direction already known, no side-effects inside setter
    setCheckedAssetIds((prev) =>
      allCurrentlyChecked
        ? prev.filter((id) => !allToggleIds.includes(id))   // un-check all
        : [...new Set([...prev, ...allToggleIds])],          // check all
    );

    // Flip localPreEFormMappings — allCurrentlyChecked is guaranteed defined
    mappedSelectedIds.forEach((assetIdStr) => {
      localPreEFormMappings.forEach((mapping) => {
        const asset = mapping.assets.find(
          (a) => String(a.ipsAssetId ?? '').trim() === assetIdStr,
        );
        if (!asset) return;

        const shouldToggle = allCurrentlyChecked ? asset.selected : !asset.selected;
        if (shouldToggle) {
          localOnChangeAssetId(
            asset,
            mapping.ipsLocationId,
            'active', // always open the accordion — viewer intent is always "show me this asset"
            'auto',   // signal PreEFormMapping to compute height from its DOM ref
          );
        }
      });
    });

    setTimeout(() => {
      isUpdatingFromViewerRef.current = false;
      setViewerSelectedDbIds([]);
    }, 100);
  }, [
    viewerSelectedDbIds,
    localPreEFormMappings,
    localOnChangeAssetId,
    autoCheckOnViewerSelection,
    isUpdatingFromViewerRef,
    setViewerSelectedDbIds,
    visibleAssetIdSet,
    goldAssetIds,           // ✅ needed for nonMappedSelectedIds filter
    getAssetIdsFromDbId,    // ✅ stable useCallback from hook — safe to include
    checkedAssetIdsRef,     // ✅ source of truth for non-mapped toggle direction
    // checkedAssetIds      ✅ removed — direction derived from ref/assetSelectedMap
  ]);

  // ─────────────────────────────────────────────────────────────
  // Save flow
  // ─────────────────────────────────────────────────────────────
  const handleSaveClick        = useCallback(() => setSaveConfirmOpen(true), []);
  const handleCancelSave       = useCallback(() => setSaveConfirmOpen(false), []);
  const handlePreExceptionBack = useCallback(() => setShow(false), [setShow]);

  // ✅ handleSaveSuccess already calls setSaveConfirmOpen(false) before setShow(),
  //    so onConfirm is no longer passed to SaveConfirmModal — it was redundant
  //    and risked a state update on an already-unmounting component.
  // ✅ handleSaveError removed — SaveConfirmModal makes no API call so it never
  //    fires; keeping a dead callback only misleads future readers.
  const handleSaveSuccess = useCallback(
    async (savedAssetIds = []) => {
      setSaveConfirmOpen(false);
      await onViewerSave?.(project.id, savedAssetIds, localPreEFormMappings);
      setShow();
    },
    [onViewerSave, project.id, setShow, localPreEFormMappings],
  );

  // ─────────────────────────────────────────────────────────────
  // Render
  //
  // Token-expiry UX decision tree:
  //   loading      → full-pane spinner (first fetch only)
  //   isRefreshing → unobtrusive banner; viewer stays alive
  //   hard error   → error panel + "Try again" button via retry()
  //   no token     → error panel (no retry — not a transient failure)
  //   ready        → normal viewer layout
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="interactive-review-page">

      {saveConfirmOpen && (
        <SaveConfirmModal
          assetIdToNameRef={assetIdToNameRef}
          assetIdToCalloutNumberRef={assetIdToCalloutNumberRef}
          saveVisibleText={saveVisibleText}
          // ✅ onConfirm removed — handleSaveSuccess owns setSaveConfirmOpen(false)
          onCancel={handleCancelSave}
          preEForm={preEForm}
          project={project}
          visiblePreEFormMappings={visibleMappings}
          localPreEFormMappings={localPreEFormMappings}
          checkedAssetIds={checkedAssetIds}
          initialCheckedAssetIds={initialCheckedAssetIds}
          onSaveSuccess={handleSaveSuccess}
          // ✅ onSaveError removed — SaveConfirmModal makes no API call
        />
      )}

      {/* ── Initial load spinner ──────────────────────────────── */}
      {loading && (
        <ViewerStatusMessage message="Loading 3D viewer…" />
      )}

      {/* ── Hard token / URN failure ──────────────────────────── */}
      {/* onRetry is only offered for token errors — a missing URN  */}
      {/* is a configuration problem that a retry cannot fix.       */}
      {!loading && (error || !token || !urlSafeUrn) && (
        <ViewerStatusMessage
          isError
          message={getViewerErrorMessage({ error, urlSafeUrn, urn })}
          onRetry={error ? retry : undefined}
        />
      )}

      {/* ── Viewer ready ─────────────────────────────────────── */}
      {!loading && !error && token && urlSafeUrn && (
        <div className="viewer-content-wrapper">

          <div className="viewer-sidebar">
            <div className="sidebar-scrollable">
              <IPSLocations
                preEForm={preEForm}
                preEFormMappings={localPreEFormMappings}
                onChangeAssetId={localOnChangeAssetId}
                selectAllAssets={localSelectAllAssets}
                saveVisibleText={saveVisibleText}
                attestView={true}
                disabled={isReadOnly}
                selectableAssetIds={goldAssetIds}
                onSelectionChange={handleScrollableSelection}
                onAssetCheckboxChange={handleAssetCheckboxChange}
              />

              {visibleMappings.length === 0 && goldAssetIds.size > 0 && (
                <div className="viewer-unmapped-notice">
                  ℹ️ This drawing contains {goldAssetIds.size} selectable
                  asset{goldAssetIds.size !== 1 ? 's' : ''}, but none match
                  the current preform mappings. You can still select and check
                  assets in the 3D viewer.
                </div>
              )}

              <ViewerLegend
                goldAssetIds={goldAssetIds}
                mappedAssetIds={mappedAssetIds}
                visibleMappings={visibleMappings}
                viewerCheckedCount={viewerCheckedCount}
                hasPartialGroups={hasPartialGroups}
              />
            </div>

            <div className="viewer-actions">
              <ViewerActions
                isReadOnly={isReadOnly}
                saveButtonText={saveButtonText}
                projectStatus={project.projectStatus}
                onSave={handleSaveClick}
                onClose={handlePreExceptionBack}
              />
            </div>
          </div>

          <div className="viewer-main-container">
            <div className="viewer-wrapper">

              {/* Silent background refresh banner — floats above the  */}
              {/* 3D canvas. Viewer stays fully functional underneath. */}
              {isRefreshing && <ViewerRefreshBanner />}

              {/* DragSelectTooltip anchored inside the viewer pane so  */}
              {/* it floats over the 3D canvas without affecting sidebar */}
              {/* or legend layout. platform is stable (computed once    */}
              {/* via useMemo at modal mount) so this child never        */}
              {/* re-renders due to platform recalculation.              */}
              <DragSelectTooltip platform={platform} />

              {/* getToken replaces the former static token prop.        */}
              {/* The Viewer component holds it in a ref internally so   */}
              {/* APS always calls the latest getter without a re-mount. */}
              <Viewer
                urn={urlSafeUrn}
                getToken={getToken}
                onViewerReady={handleViewerReady}
              />
            </div>
          </div>

        </div>
      )}

    </div>
  );
};

export default InteractiveViewerModal;